import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const LINES = [
  { text: "Every road.", cls: "text-bone" },
  { text: "One call.", cls: "text-signal" },
  { text: "Zero hassle.", cls: "text-bone" },
];
const EASE = [0.76, 0, 0.24, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1542705959-878ca346eb20?q=85&w=2400&auto=format&fit=crop";

const STATS = [
  ["05", "Services on tap"],
  ["24×7", "Dispatch desk"],
  ["04", "Vehicle categories"],
  ["100%", "Meter-honest rides"],
];

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div className="absolute inset-x-0 -top-[15%] h-[130%]" style={{ y: reduce ? 0 : y }} aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/35" />
      </motion.div>

      <motion.div style={{ opacity: reduce ? 1 : fade }} className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-12 pt-40 pb-0">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.32em] text-signal mb-6"
        >
          Chhattisgarh · Jharkhand · West Bengal · All over Odisha
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-[14.5vw] sm:text-[11vw] lg:text-[8.6vw]">
          {LINES.map((l, i) => (
            <span key={l.text} className="block overflow-hidden">
              <motion.span
                className={`block ${l.cls}`}
                initial={reduce ? false : { y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.95, ease: EASE, delay: 0.18 + i * 0.13 }}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 0.7 }}
          className="origin-left h-[3px] w-40 bg-signal mt-8 mb-8"
        />

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed"
        >
          Hourly rentals, long-distance journeys, local intercity runs and
          airport-to-airport transfers. Clean sedans, tough SUVs, honest
          meters — one call and the road is yours.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="flex flex-wrap items-center gap-4 mt-10 mb-16"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => onBook()}
            className="group inline-flex items-center gap-3 bg-signal text-ink font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 hover:bg-bone transition-colors duration-200"
          >
            Book a Ride
            <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-fleet-link"
            onClick={() => scrollToId("fleet")}
            className="inline-flex items-center border border-white/25 text-bone font-mono text-xs uppercase tracking-[0.22em] px-8 py-4 hover:border-signal hover:text-signal transition-colors duration-200"
          >
            See the Fleet
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="border-t border-white/10 grid grid-cols-2 md:grid-cols-4"
        >
          {STATS.map(([num, label], i) => (
            <div
              key={label}
              className={`py-6 pr-6 ${i > 0 ? "md:border-l md:border-white/10 md:pl-8" : ""} ${i % 2 === 1 ? "border-l border-white/10 pl-6 md:pl-8" : ""}`}
            >
              <div className="font-mono text-2xl md:text-3xl text-signal">{num}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">{label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
